import { Alert, Box, Link as MuiLink, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { defaultDownlineQuery, memberRepositoryToken } from './repository';
import type { DirectDownline } from './types';
import { MemberStatusChip } from './MemberListPage';

export function DirectDownlinesPanel({ memberId }: { readonly memberId: string }) {
  const repository = useRepository(memberRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(defaultDownlineQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.listDownlines(memberId, query, signal),
    [memberId, query, refreshKey, repository],
    (page) => page.items.length === 0,
  );
  const columns = useMemo<readonly GridColDef<DirectDownline>[]>(
    () => [
      {
        field: 'username',
        headerName: t('members.fields.username'),
        minWidth: 180,
        renderCell: ({ row }) => (
          <MuiLink component={Link} to={`/members/${row.id}`}>
            {row.username ?? row.id}
          </MuiLink>
        ),
      },
      {
        field: 'partnerUserId',
        headerName: t('members.fields.partnerUserId'),
        minWidth: 150,
        sortable: false,
        valueGetter: (_value, row) => row.partnerUserId ?? t('common.notAvailable'),
      },
      {
        field: 'agent',
        headerName: t('members.fields.agent'),
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <MuiLink component={Link} to={`/agents/${row.agent.id}`}>
            {row.agent.code}
          </MuiLink>
        ),
      },
      {
        field: 'status',
        headerName: t('members.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => <MemberStatusChip status={row.status} />,
      },
      {
        field: 'registeredAt',
        headerName: t('members.fields.registeredAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );
  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>{t('members.downlines.directOnly')}</strong> {t('members.downlines.deeper')}
      </Alert>
      <PageState
        emptyMessage={t('members.downlines.empty')}
        error={toPageError(resource.error, t('members.errors.downlines'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Box
            aria-label={t('members.downlines.gridLabel')}
            role="region"
            sx={{ maxWidth: '100%', overflowX: 'auto' }}
            tabIndex={0}
          >
            <ServerDataGrid
              columns={columns}
              disableRowSelectionOnClick
              onQueryChange={(next) => setQuery({ ...next, page: next.page + 1 })}
              page={resource.data}
              query={{ ...query, page: query.page - 1 }}
              sx={{ minWidth: 850 }}
            />
          </Box>
        )}
      </PageState>
    </Stack>
  );
}
